/**
 * What "New" can do, and who currently knows how to do it.
 *
 * The sidebar's New menu lives in `Shell`, which is rendered on every route.
 * The things it offers — a folder, an upload — need the folder you are
 * standing in, and only `Files` knows that. So `Files` registers the three
 * actions while it is mounted and the menu calls whichever are there.
 *
 * A module-level store rather than a value on `AppContext`, deliberately:
 * `Files` re-registers whenever the current folder changes, and putting that
 * on the app context would re-render the whole shell — the nav, the quota
 * footer, the bell — every time somebody opened a folder.
 *
 * When nothing is registered, the menu stashes what was asked for and
 * navigates to the file list, which picks it up as it mounts.
 */

import { useSyncExternalStore } from 'react';

export interface NewActions {
  newFolder: () => void;
  uploadFiles: () => void;
  uploadFolder: () => void;
}

export type NewActionKind = keyof NewActions;

let actions: NewActions | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

/** Called by the file list on mount, and again whenever the folder changes. */
export function registerNewActions(next: NewActions | null): void {
  actions = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => actions;

export function useNewActions(): NewActions | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * The action to run once the file list has mounted.
 *
 * Read once and cleared, so a stale intent cannot fire on a later visit.
 */
let pending: NewActionKind | null = null;

export const setPendingNewAction = (kind: NewActionKind): void => {
  pending = kind;
};

export function takePendingNewAction(): NewActionKind | null {
  const kind = pending;
  pending = null;
  return kind;
}
